import { useState } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { c, money, computeLine, computeTotals, inputStyle, labelStyle } from './cajeroShared';

const emptyItem = () => ({
  ref: '', kind: null, name: '', description: '',
  quantity: 1, unit_price: '', iva_percent: '', retefuente_percent: '', impoconsumo_percent: '',
});

function Field({ label, children, style }) {
  return <div style={style}><label style={labelStyle}>{label}</label>{children}</div>;
}

// Combina los datos prellenados (p. ej. de un pedido) con la línea vacía por
// defecto, para que los campos no provistos (impuestos, kind, etc.) existan.
const seedItems = (arr) => (Array.isArray(arr) && arr.length ? arr.map((it) => ({ ...emptyItem(), ...it })) : [emptyItem()]);

function DocEditor({ token, type, typeLabel, hasPayment, onBack, initialCustomer, initialItems, billId, appointmentId }) {
  const isMobile = useIsMobile();
  const [customer, setCustomer] = useState(() => ({ doc: '', name: '', address: '', phone: '', email: '', ...(initialCustomer || {}) }));
  const [items, setItems] = useState(() => seedItems(initialItems));
  const [paymentType, setPaymentType] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState(null);
  // Ventanas flotantes de coincidencias del buscador.
  const [clientPopup, setClientPopup] = useState(null);   // { loading, matches }
  const [productPopup, setProductPopup] = useState(null); // { index, loading, matches }

  const authH = { Authorization: `Bearer ${token}` };
  const jsonH = { ...authH, 'Content-Type': 'application/json' };

  const totals = computeTotals(items);
  const change = paymentType === 'cash' && cashReceived !== '' ? (parseFloat(cashReceived) || 0) - totals.total : null;

  const setItem = (i, patch) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (i) => setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  // Buscar clientes por cédula o nombre → ventana flotante de coincidencias.
  const searchClients = async () => {
    const q = customer.doc.trim();
    if (!q) return;
    setClientPopup({ loading: true, matches: [] });
    try {
      const r = await fetch(`/api/cajero/contacts/search?type=${type}&q=${encodeURIComponent(q)}`, { headers: authH });
      const { data } = await r.json();
      setClientPopup({ loading: false, matches: data || [] });
    } catch { setClientPopup({ loading: false, matches: [] }); }
  };

  // Al elegir un cliente de la lista, autocompletar sus datos guardados.
  const pickClient = (m) => {
    setCustomer((prev) => ({
      ...prev,
      doc: m.doc || prev.doc,
      name: m.nombre || prev.name, address: m.direccion || prev.address,
      phone: m.celular || prev.phone, email: m.correo || prev.email,
    }));
    setClientPopup(null);
    setMsg({ ok: true, text: 'Cliente autocompletado.' });
    setTimeout(() => setMsg(null), 3000);
  };

  // Buscar productos/servicios por nombre o código → ventana flotante.
  const searchProducts = async (i) => {
    const q = (items[i].ref || '').trim();
    if (!q) return;
    setProductPopup({ index: i, loading: true, matches: [] });
    try {
      const r = await fetch(`/api/cajero/products/search?q=${encodeURIComponent(q)}`, { headers: authH });
      const { data } = await r.json();
      setProductPopup({ index: i, loading: false, matches: data || [] });
    } catch { setProductPopup({ index: i, loading: false, matches: [] }); }
  };

  // Al elegir un producto/servicio, autocompletar la línea.
  const pickProduct = (m) => {
    const i = productPopup?.index;
    if (i == null) return;
    setItem(i, {
      ref: m.ref || items[i].ref, kind: m.kind, name: m.name || '',
      description: m.description || '', unit_price: m.price != null ? m.price : items[i].unit_price,
    });
    setProductPopup(null);
  };

  const submit = async () => {
    setCreating(true);
    setMsg(null);
    try {
      const payload = {
        type,
        customer,
        items: items
          .filter((it) => it.name || it.unit_price || it.ref)
          .map((it) => ({
            product_ref: it.ref || null, name: it.name, description: it.description,
            quantity: it.quantity, unit_price: it.unit_price,
            iva_percent: it.iva_percent, retefuente_percent: it.retefuente_percent, impoconsumo_percent: it.impoconsumo_percent,
          })),
      };
      if (hasPayment) { payload.payment_type = paymentType; if (paymentType === 'cash') payload.cash_received = cashReceived === '' ? null : cashReceived; }
      if (billId) payload.bill_id = billId; // ligar al pedido de origen
      if (appointmentId) payload.appointment_id = appointmentId; // ligar a la cita de origen

      const r = await fetch('/api/cajero/docs', { method: 'POST', headers: jsonH, body: JSON.stringify(payload) });
      const data = await r.json();
      if (r.ok) setResult(data);
      else setMsg({ ok: false, text: data.message || 'No se pudo crear' });
    } catch {
      setMsg({ ok: false, text: 'Error de conexión' });
    } finally {
      setCreating(false);
    }
  };

  const print = async () => {
    if (!result) return;
    try {
      const r = await fetch(`/api/cajero/docs/${type}/${result.id}/print`, { headers: authH });
      const data = await r.json();
      if (r.ok && data.url) window.open(data.url, '_blank');
      else setMsg({ ok: false, text: data.message || 'No se pudo imprimir' });
    } catch { setMsg({ ok: false, text: 'Error al imprimir' }); }
  };

  // ── Pantalla de confirmación tras crear ──
  if (result) {
    return (
      <div style={{ maxWidth: 620, margin: '0 auto', padding: '2rem 1rem', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem' }}>✅</div>
        <h2 style={{ color: c.primary, margin: '8px 0' }}>{typeLabel} creada</h2>
        <p style={{ color: c.text, fontSize: '1.05rem' }}>No. <strong>{result.full_number}</strong> · Total {money(result.total)}</p>
        {result.change_given != null && (
          <p style={{ color: c.textMuted }}>Cambio a devolver: <strong style={{ color: c.success }}>{money(result.change_given)}</strong></p>
        )}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px', flexWrap: 'wrap' }}>
          <button onClick={print} style={btn(c.primary, c.background)}>🖨 Imprimir</button>
          {!billId && (
            <button onClick={() => { setResult(null); setItems([emptyItem()]); setCustomer({ doc: '', name: '', address: '', phone: '', email: '' }); setCashReceived(''); }} style={btnOutline}>Crear otra</button>
          )}
          <button onClick={onBack} style={btnOutline}>Volver</button>
        </div>
        {msg && <p style={{ color: msg.ok ? c.success : c.error, marginTop: '12px' }}>{msg.text}</p>}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '1.25rem 1rem 4rem' }}>
      <button onClick={onBack} style={{ ...btnOutline, marginBottom: '14px' }}>← Volver</button>
      <h1 style={{ color: c.primary, fontSize: '1.4rem', margin: '0 0 16px' }}>Nueva {typeLabel.toLowerCase()}</h1>

      {billId && (
        <div style={{ background: 'rgba(56,189,248,0.12)', border: '1px solid #38bdf8', color: c.text, borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '0.86rem' }}>
          📦 Estás facturando el <strong>pedido #{billId}</strong>. Completa los datos faltantes y al crear la {typeLabel.toLowerCase()} el pedido quedará ligado a este documento.
        </div>
      )}

      {/* Cliente */}
      <section style={card}>
        <h3 style={sectionTitle}>Cliente <span style={{ color: c.textMuted, fontWeight: 400, fontSize: '0.8rem' }}>(opcional)</span></h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '10px' }}>
          <Field label="Cédula / NIT o nombre" style={{ flex: 1 }}>
            <input style={inputStyle} value={customer.doc} onChange={(e) => setCustomer({ ...customer, doc: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && searchClients()} placeholder="Escribe cédula o nombre y busca" />
          </Field>
          <button onClick={searchClients} style={{ ...btn(c.primary, c.background), whiteSpace: 'nowrap' }}>Buscar</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 14px' }}>
          <Field label="Nombre"><input style={inputStyle} value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} /></Field>
          <Field label="Dirección"><input style={inputStyle} value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} /></Field>
          <Field label="Celular"><input style={inputStyle} value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} /></Field>
          <Field label="Correo"><input style={inputStyle} value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} /></Field>
        </div>
        {msg && !result && <p style={{ color: msg.ok ? c.success : c.error, fontSize: '0.82rem', margin: '8px 0 0' }}>{msg.text}</p>}
      </section>

      {/* Productos / servicios */}
      <section style={card}>
        <h3 style={sectionTitle}>Productos / servicios</h3>
        {items.map((it, i) => {
          const l = computeLine(it);
          return (
            <div key={i} style={{ border: `1px solid ${c.border}`, borderRadius: '10px', padding: '12px', marginBottom: '12px', background: c.background }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                <Field label="Referencia, código o nombre" style={{ flex: 1 }}>
                  <input style={inputStyle} value={it.ref} onChange={(e) => setItem(i, { ref: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && searchProducts(i)} placeholder="Escribe código o nombre y busca" />
                </Field>
                <button onClick={() => searchProducts(i)} style={{ ...btnOutline, whiteSpace: 'nowrap' }}>Buscar</button>
                {items.length > 1 && <button onClick={() => removeItem(i)} style={{ ...btnOutline, borderColor: c.error, color: c.error }}>✕</button>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 2fr', gap: '0 14px', marginTop: '6px' }}>
                <Field label="Nombre"><input style={inputStyle} value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} /></Field>
                <Field label="Descripción"><input style={inputStyle} value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} /></Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: '0 10px' }}>
                <Field label="Cantidad"><input type="number" onWheel={(e) => e.currentTarget.blur()} min="0" style={inputStyle} value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} /></Field>
                <Field label="Precio unit."><input type="number" onWheel={(e) => e.currentTarget.blur()} min="0" style={inputStyle} value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} /></Field>
                <Field label="IVA %"><input type="number" onWheel={(e) => e.currentTarget.blur()} min="0" style={inputStyle} value={it.iva_percent} onChange={(e) => setItem(i, { iva_percent: e.target.value })} /></Field>
                <Field label="Retefuente %"><input type="number" onWheel={(e) => e.currentTarget.blur()} min="0" style={inputStyle} value={it.retefuente_percent} onChange={(e) => setItem(i, { retefuente_percent: e.target.value })} /></Field>
                <Field label="Imp. consumo %"><input type="number" onWheel={(e) => e.currentTarget.blur()} min="0" style={inputStyle} value={it.impoconsumo_percent} onChange={(e) => setItem(i, { impoconsumo_percent: e.target.value })} /></Field>
              </div>

              {/* Totales por línea (horizontal) */}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px', fontSize: '0.82rem', color: c.textMuted, borderTop: `1px dashed ${c.border}`, paddingTop: '8px' }}>
                <span>Subtotal: <strong style={{ color: c.text }}>{money(l.subtotal)}</strong></span>
                <span>Impuesto: <strong style={{ color: c.text }}>{money(l.tax)}</strong></span>
                <span>Total: <strong style={{ color: c.primary }}>{money(l.total)}</strong></span>
              </div>
            </div>
          );
        })}
        <button onClick={addItem} style={btnOutline}>+ Agregar línea</button>
      </section>

      {/* Pago (solo factura/pos) */}
      {hasPayment && (
        <section style={card}>
          <h3 style={sectionTitle}>Pago</h3>
          <div style={{ display: 'flex', gap: '18px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', gap: '6px', cursor: 'pointer' }}>
              <input type="radio" checked={paymentType === 'cash'} onChange={() => setPaymentType('cash')} /> Efectivo
            </label>
            <label style={{ display: 'flex', gap: '6px', cursor: 'pointer' }}>
              <input type="radio" checked={paymentType === 'credit'} onChange={() => setPaymentType('credit')} /> Crédito
            </label>
          </div>
          {paymentType === 'cash' && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Field label="¿Con cuánto paga?" style={{ maxWidth: 220 }}>
                <input type="number" onWheel={(e) => e.currentTarget.blur()} min="0" style={inputStyle} value={cashReceived} onChange={(e) => setCashReceived(e.target.value)} />
              </Field>
              {change != null && (
                <div style={{ fontSize: '0.95rem' }}>Cambio: <strong style={{ color: change < 0 ? c.error : c.success }}>{money(change)}</strong>
                  {change < 0 && <span style={{ color: c.error, fontSize: '0.8rem' }}> (falta)</span>}</div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Totales del documento (vertical, acumulado) */}
      <section style={{ ...card, position: 'sticky', bottom: 0 }}>
        <div style={{ maxWidth: 320, marginLeft: 'auto', fontSize: '0.9rem' }}>
          <Row l="Subtotal" v={money(totals.subtotal)} />
          <Row l="IVA" v={money(totals.iva)} />
          {totals.impo > 0 && <Row l="Imp. consumo" v={money(totals.impo)} />}
          {totals.rete > 0 && <Row l="Retefuente (-)" v={money(totals.rete)} />}
          <div style={{ borderTop: `1px solid ${c.border}`, margin: '6px 0' }} />
          <Row l="TOTAL" v={money(totals.total)} bold />
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px', flexWrap: 'wrap' }}>
          {msg && !msg.ok && <span style={{ color: c.error, alignSelf: 'center' }}>{msg.text}</span>}
          <button onClick={submit} disabled={creating} style={{ ...btn(c.primary, c.background), opacity: creating ? 0.6 : 1, padding: '12px 28px', fontSize: '1rem' }}>
            {creating ? 'Creando…' : `Crear ${typeLabel.toLowerCase()}`}
          </button>
        </div>
      </section>

      {clientPopup && (
        <MatchPopup title="Clientes encontrados" loading={clientPopup.loading} matches={clientPopup.matches}
          onClose={() => setClientPopup(null)} onPick={pickClient}
          renderItem={(m) => (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <span style={{ color: c.text }}>{m.nombre || 'Sin nombre'}</span>
              <span style={{ color: c.textMuted, fontSize: '0.82rem' }}>{m.doc}</span>
            </div>
          )} />
      )}

      {productPopup && (
        <MatchPopup title="Productos / servicios encontrados" loading={productPopup.loading} matches={productPopup.matches}
          onClose={() => setProductPopup(null)} onPick={pickProduct}
          renderItem={(m) => (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <span style={{ color: c.text }}>{m.kind === 'service' ? '🛠' : '📦'} {m.name}{m.ref ? <span style={{ color: c.textMuted, fontSize: '0.8rem' }}> · {m.ref}</span> : null}</span>
              <strong style={{ color: c.primary }}>{money(m.price)}</strong>
            </div>
          )} />
      )}
    </div>
  );
}

// Ventana flotante de coincidencias: fondo oscuro que cierra al hacer clic fuera,
// botón ✕ para cerrar y lista de resultados clicables.
function MatchPopup({ title, loading, matches, onPick, onClose, renderItem }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '10vh 16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px', width: '100%', maxWidth: 520, maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: `1px solid ${c.border}` }}>
          <strong style={{ color: c.text }}>{title}</strong>
          <button onClick={onClose} aria-label="Cerrar" style={{ background: 'none', border: 'none', color: c.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: '8px', overflowY: 'auto' }}>
          {loading ? (
            <p style={{ color: c.textMuted, padding: '12px' }}>Buscando…</p>
          ) : matches.length === 0 ? (
            <p style={{ color: c.textMuted, padding: '12px' }}>Sin coincidencias. Puedes escribir los datos manualmente.</p>
          ) : (
            matches.map((m, idx) => (
              <div key={idx} onClick={() => onPick(m)} style={{ cursor: 'pointer', padding: '10px 12px', borderRadius: '8px', marginBottom: '4px' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = c.background; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                {renderItem(m)}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ l, v, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontWeight: bold ? 700 : 400, fontSize: bold ? '1.05rem' : '0.9rem', color: bold ? c.primary : c.text }}>
      <span style={{ color: bold ? c.primary : c.textMuted }}>{l}</span><span>{v}</span>
    </div>
  );
}

const card = { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '18px', marginBottom: '14px' };
const sectionTitle = { color: c.text, fontSize: '1rem', margin: '0 0 12px' };
const btnOutline = { background: 'none', border: `1px solid ${c.border}`, color: c.text, borderRadius: '7px', padding: '9px 14px', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit' };
function btn(bg, fg) { return { background: bg, border: 'none', color: fg, borderRadius: '7px', padding: '9px 16px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'inherit' }; }

export default DocEditor;
